import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { trackEvent, mapApiErrorToCode } from '../services/analytics';
import type { CommentPinType, PublicComment } from '../types';
import type { PinType } from '../services/analytics';

const DISPLAY_NAME_REGEX = /^[A-Za-z0-9 _-]+$/;

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.max(0, (Date.now() - t) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function bodyLengthBucket(len: number): 'short' | 'medium' | 'long' {
  if (len < 60) return 'short';
  if (len < 200) return 'medium';
  return 'long';
}

interface CommentsSectionProps {
  pinType: PinType;
  pinTypeApi: CommentPinType;
  pinId: string;
}

export default function CommentsSection({ pinType, pinTypeApi, pinId }: CommentsSectionProps) {
  const [comments, setComments] = useState<PublicComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reportedIds, setReportedIds] = useState<Record<string, boolean>>({});
  const submitErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
      .getComments(pinTypeApi, pinId, 20)
      .then((rows) => {
        if (cancelled) return;
        setComments(rows);
        trackEvent('comment_thread_opened', { pin_type: pinType, comment_count: rows.length });
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load comments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pinType, pinTypeApi, pinId]);

  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submitError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const trimmedBody = body.trim();
    if (!trimmedBody) {
      setSubmitError('Comment cannot be empty');
      return;
    }
    const trimmedName = displayName.trim();
    if (trimmedName && !DISPLAY_NAME_REGEX.test(trimmedName)) {
      setSubmitError('Display name may only contain letters, numbers, spaces, _ and -');
      return;
    }
    setSubmitting(true);
    trackEvent('comment_form_submitted', {
      pin_type: pinType,
      has_display_name: trimmedName.length > 0,
      body_length_bucket: bodyLengthBucket(trimmedBody.length),
    });
    try {
      const created = await api.createComment(pinTypeApi, pinId, trimmedBody, trimmedName || undefined);
      setComments((prev) => [created, ...prev]);
      setBody('');
      setDisplayName('');
      trackEvent('comment_form_result', { pin_type: pinType, result: 'success' });
    } catch (err) {
      const code = mapApiErrorToCode(err);
      const result = code === 'rate_limit' ? 'rate_limited' : code;
      trackEvent('comment_form_result', { pin_type: pinType, result });
      setSubmitError(
        result === 'rate_limited'
          ? "You've posted too many comments. Please try again later."
          : result === 'network'
          ? 'Network error. Please check your connection and try again.'
          : err instanceof Error
          ? err.message
          : 'Failed to post comment'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async (commentId: string) => {
    if (reportedIds[commentId]) return;
    try {
      await api.reportComment(commentId);
      setReportedIds((prev) => ({ ...prev, [commentId]: true }));
      trackEvent('comment_reported', { pin_type: pinType, result: 'success' });
    } catch (err) {
      const code = mapApiErrorToCode(err);
      const result = code === 'rate_limit' ? 'rate_limited' : code === 'validation' ? 'self_report' : code;
      trackEvent('comment_reported', { pin_type: pinType, result });
    }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <p className="text-sm text-gray-500 text-center py-4">Loading comments…</p>
      ) : loadError ? (
        <p className="text-sm text-red-600 text-center py-4">{loadError}</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No comments yet. Be the first to share what you know about this pin.
        </p>
      ) : (
        <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
          {comments.map((c) => (
            <li key={c.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span className="font-semibold text-gray-800">
                  {c.display_name || 'Anonymous'}
                </span>
                <div className="flex items-center gap-2">
                  <span>{formatRelative(c.created_at)}</span>
                  <button
                    type="button"
                    onClick={() => handleReport(c.id)}
                    disabled={reportedIds[c.id]}
                    className="text-gray-400 hover:text-red-600 disabled:text-red-400 disabled:cursor-default"
                    title={reportedIds[c.id] ? 'Reported' : 'Report this comment'}
                    aria-label="Report this comment"
                  >
                    🚩
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{c.body}</p>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="border-t pt-3 space-y-2">
        {submitError && (
          <div
            ref={submitErrorRef}
            className="bg-red-50 text-red-800 text-sm p-2.5 rounded-lg border border-red-200"
          >
            {submitError}
          </div>
        )}
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={40}
          disabled={submitting}
          placeholder="Display name (optional) — leave blank for Anonymous"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          disabled={submitting}
          rows={3}
          placeholder="Share a tip, ask a question, or note something useful about this pin. No links or contact info — those get auto-removed."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-parksy-500 focus:border-transparent disabled:opacity-60 resize-none"
        />
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>Anonymous by default. Be respectful.</span>
          <span>{body.length}/500</span>
        </div>
        <button
          type="submit"
          disabled={submitting || body.trim().length === 0}
          className="w-full px-4 py-2 text-white rounded-lg text-sm font-medium bg-parksy-600 hover:bg-parksy-700 disabled:opacity-50"
        >
          {submitting ? 'Posting…' : 'Post comment'}
        </button>
      </form>
    </div>
  );
}
